<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const fieldTable = [
  { field: 'Kode Voucher', desc: 'Kode yang diketik pelanggan/kasir saat checkout, mis. "HEMAT10". Kosongkan saat membuat untuk digenerate acak otomatis oleh sistem.', wajib: 'Opsional (auto)' },
  { field: 'Tipe Diskon', desc: 'Persentase (%) dari total, atau Nominal (Rp) tetap — sama seperti Promo.', wajib: '✅ Ya' },
  { field: 'Nilai', desc: 'Besaran diskonnya — mis. 10 (untuk 10%) atau 10000 (untuk Rp10.000).', wajib: '✅ Ya' },
  { field: 'Min. Pembelian', desc: 'Voucher baru bisa dipakai kalau subtotal belanja sudah mencapai nominal ini.', wajib: 'Opsional' },
  { field: 'Maks. Potongan', desc: 'Batas atas nominal potongan — penting untuk tipe Persentase, sama alasannya dengan Promo (lihat panduan Promo & Diskon).', wajib: 'Opsional' },
  { field: 'Kuota Total', desc: 'Berapa kali kode ini boleh dipakai TOTAL oleh semua pelanggan. Kosongkan = tidak terbatas.', wajib: 'Opsional' },
  { field: 'Maks. Pemakaian per Customer', desc: 'Berapa kali SATU pelanggan (yang sama) boleh pakai kode ini. Hanya berlaku kalau transaksinya terhubung ke data Customer terdaftar.', wajib: '✅ Ya (default 1)' },
  { field: 'Mulai / Berakhir', desc: 'Rentang tanggal voucher berlaku. Kosongkan kalau mau berlaku terus.', wajib: 'Opsional' },
]

const faq = [
  {
    q: 'Apa bedanya Voucher dengan Promo?',
    a: 'Promo aktif berdasarkan aturan yang dicek sistem sendiri (tanggal, jam, minimal belanja) — bisa otomatis tanpa kode apa pun. Voucher WAJIB kode yang diketik manual saat checkout, biasanya dibagikan lewat media promosi tertentu (Instagram, WhatsApp, brosur) sehingga Anda bisa mengontrol siapa saja yang tahu kodenya dan membatasi kuota pemakaiannya.',
  },
  {
    q: 'Kode voucher saya sudah benar tapi tetap ditolak, kenapa?',
    a: 'Beberapa kemungkinan: (1) Voucher belum aktif atau sudah lewat tanggal berakhir, (2) subtotal belanja belum mencapai Min. Pembelian, (3) Kuota Total voucher ini sudah habis dipakai, atau (4) pelanggan ini sudah pernah pakai kode yang sama sebanyak batas Maks. Pemakaian per Customer. Pesan error yang muncul di layar kasir akan menyebutkan alasan spesifiknya.',
  },
  {
    q: 'Batas "pemakaian per customer" berlaku untuk transaksi tanpa data pelanggan (walk-in)?',
    a: 'Tidak. Batas ini hanya bisa dicek kalau transaksi terhubung ke data Customer yang terdaftar di sistem (dipilih/didaftarkan saat checkout). Untuk transaksi tanpa data pelanggan, sistem tidak bisa tahu "siapa" yang sudah pakai kode itu sebelumnya — jadi hanya Kuota Total yang berlaku sebagai pembatas.',
  },
  {
    q: 'Bisa membuat kode voucher sendiri, misalnya "LEBARAN2026"?',
    a: 'Bisa. Isi kolom Kode Voucher sesuai keinginan saat membuat. Kalau dikosongkan, sistem akan men-generate kode acak otomatis untuk Anda.',
  },
  {
    q: 'Kode voucher bisa diubah setelah dibuat?',
    a: 'Tidak — kode voucher terkunci (tidak bisa diedit) setelah dibuat, supaya kode yang sudah terlanjur dibagikan ke pelanggan tidak berubah sendiri. Kalau perlu kode baru, buat voucher baru dan nonaktifkan yang lama.',
  },
  {
    q: 'Voucher yang sudah pernah dipakai pelanggan, bisa dihapus?',
    a: 'Sama seperti Promo — voucher yang sudah pernah dipakai di sebuah transaksi tidak benar-benar dihapus (supaya riwayat transaksi lama tetap lengkap), hanya dinonaktifkan. Voucher yang belum pernah dipakai sama sekali akan benar-benar terhapus.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Voucher &amp; Kupon</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            size="small"
            class="mb-4"
          >
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="primary"
            variant="tonal"
            size="64"
            rounded="lg"
            class="me-4"
          >
            <VIcon
              icon="ri-coupon-3-line"
              size="36"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Voucher &amp; Kupon
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Buat kode diskon yang dibagikan ke pelanggan, dengan kuota pemakaian yang terkontrol
            </p>
          </div>
        </div>
        <VAlert
          type="info"
          variant="tonal"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda membuat kode Voucher/Kupon dan cara pelanggan/kasir memakainya
          saat checkout di POS.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-book-open-line"
              class="me-2"
              color="primary"
            />
            Apa itu Voucher/Kupon?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Voucher/Kupon</strong> adalah kode diskon yang harus <strong>diketik manual</strong>
              saat checkout, biasanya Anda bagikan lewat media tertentu — story Instagram,
              broadcast WhatsApp, brosur cetak, atau kartu member. Berbeda dari Promo yang bisa
              berjalan otomatis, Voucher perlu kode yang cuma diketahui orang yang benar-benar
              menerima promosi Anda.
            </p>
            <VAlert
              type="success"
              variant="tonal"
              class="mb-0"
            >
              <strong>Kapan pakai Voucher, bukan Promo?</strong>
              Kalau Anda ingin mengontrol siapa saja yang bisa pakai diskon (mis. hanya follower
              Instagram yang tahu kodenya) dan membatasi berapa kali kode itu boleh dipakai —
              gunakan Voucher. Kalau ingin diskon berlaku untuk semua pembeli tanpa syarat kode,
              gunakan Promo.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Membuat -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-play-circle-line"
              class="me-2"
              color="success"
            />
            Cara Membuat Voucher Baru
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-0">
              <li class="mb-3">
                Buka menu <strong>Marketing → Voucher &amp; Kupon</strong> dari sidebar kiri.
              </li>
              <li class="mb-3">
                Klik tombol <strong>"Tambah Voucher"</strong> di pojok kanan atas.
              </li>
              <li class="mb-3">
                Isi <strong>Kode Voucher</strong> sesuai keinginan (mis. "HEMAT10"), atau
                <strong>kosongkan</strong> supaya sistem membuatkan kode acak otomatis untuk Anda.
              </li>
              <li class="mb-3">
                Pilih <strong>Tipe Diskon</strong> (Persentase atau Nominal) lalu isi <strong>Nilai</strong>-nya.
              </li>
              <li class="mb-3">
                (Opsional) Isi <strong>Min. Pembelian</strong> dan <strong>Maks. Potongan</strong> kalau
                perlu — sama fungsinya dengan yang ada di Promo/Diskon.
              </li>
              <li class="mb-3">
                Atur <strong>Kuota Total</strong> (berapa kali kode ini boleh dipakai semua orang)
                dan <strong>Maks. Pemakaian per Customer</strong> (default 1x per pelanggan).
              </li>
              <li class="mb-3">
                (Opsional) Atur tanggal <strong>Mulai</strong> dan <strong>Berakhir</strong> kalau
                voucher ini untuk periode promosi tertentu saja.
              </li>
              <li class="mb-0">
                Pastikan toggle <strong>Aktif</strong> menyala, lalu klik <strong>"Simpan"</strong>.
                Bagikan kode yang muncul di daftar ke pelanggan Anda.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tabel field -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-list-check-2"
              class="me-2"
              color="info"
            />
            Penjelasan Tiap Kolom
          </VCardTitle>
          <VCardText>
            <VTable>
              <thead>
                <tr>
                  <th>Kolom</th>
                  <th>Penjelasan</th>
                  <th>Wajib?</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in fieldTable"
                  :key="row.field"
                >
                  <td><strong>{{ row.field }}</strong></td>
                  <td class="text-body-2 py-2">
                    {{ row.desc }}
                  </td>
                  <td class="text-body-2">
                    {{ row.wajib }}
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Pakai saat Checkout -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-store-2-line"
              class="me-2"
              color="success"
            />
            Cara Kasir Memakai Voucher Saat Checkout
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-3">
                Di layar <strong>POS Kasir</strong>, setelah item pelanggan sudah masuk keranjang,
                cari kolom <strong>"Kode voucher"</strong> di panel keranjang (sebelah kanan).
              </li>
              <li class="mb-3">
                Minta pelanggan sebutkan kodenya, ketik di kolom tersebut, lalu klik
                <strong>"Cek"</strong> (atau tekan Enter).
              </li>
              <li class="mb-3">
                Kalau kode <strong>valid</strong>, kolom berubah jadi chip hijau menampilkan kode
                dan besar potongannya — otomatis masuk ke ringkasan belanja sebagai baris
                <strong>"Diskon Voucher"</strong>.
              </li>
              <li class="mb-0">
                Kalau kode <strong>tidak valid</strong>, muncul keterangan singkat di bawah kolom
                (mis. "Kode voucher tidak ditemukan" atau "Kuota voucher ini sudah habis") —
                sampaikan ke pelanggan dan lanjutkan tanpa voucher, atau minta kode lain.
              </li>
            </ol>
            <VAlert
              type="warning"
              variant="tonal"
              class="mb-0 text-body-2"
            >
              Kalau kode sempat "Cek" berhasil tapi voucher-nya kebetulan habis kuota tepat
              sebelum transaksi disimpan (mis. dipakai pelanggan lain lebih dulu), sistem akan
              menolak dan membatalkan seluruh transaksi tersebut — bukan menyimpan transaksi
              tanpa diskon. Cukup ulangi proses checkout tanpa kode itu.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard
          color="success"
          variant="tonal"
        >
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-lightbulb-line"
              class="me-2"
            />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList
              density="compact"
              bg-color="transparent"
            >
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Selalu isi Kuota Total</strong> untuk voucher yang dibagikan luas (mis.
                  di media sosial publik) supaya biaya promosi tetap terkendali, tidak dipakai
                  tanpa batas.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Gunakan kode yang mudah diingat</strong> dan relevan dengan promosinya,
                  mis. "LEBARAN26" atau "ULTAH5", supaya pelanggan mudah mengetik ulang saat checkout.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Sambungkan transaksi ke data Customer</strong> kalau ingin batas
                  Maks. Pemakaian per Customer benar-benar berfungsi — voucher yang dipakai di
                  transaksi tanpa data pelanggan tidak bisa dilacak "siapa" penggunanya.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Promo & Diskon', sub: 'Diskon otomatis tanpa perlu kode', icon: 'ri-price-tag-3-line', color: 'primary', to: '/userguide/promo-voucher-loyalty/promo' },
              { title: 'Program Loyalty', sub: 'Poin & stempel untuk pelanggan setia', icon: 'ri-vip-diamond-line', color: 'secondary', to: '/userguide/promo-voucher-loyalty/loyalty' },
              { title: 'Gabungan di 1 Transaksi', sub: 'Promo + Voucher + Poin bareng-bareng', icon: 'ri-git-merge-line', color: 'success', to: '/userguide/promo-voucher-loyalty/kombinasi' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>